import { AnyAction } from '@reduxjs/toolkit'

import { FETCH_CONTEXT_ORGANIZATION_SUCCESS } from '@/components/App/actionTypes'
import { CHANGE_DEFAULT_SPACE_SUCCESS } from '@/pages/OrganizationSettingsPage/actionTypes'
import { ADD_SPACE_SUCCESS, UPDATE_SPACE_SUCCESS } from '@/pages/OrganizationSpaceEditPage/actionTypes'
import { DELETE_SPACE_SUCCESS } from '@/pages/OrganizationSpacesPage/actionTypes'
import { ADD_CUSTOM_APP_INSTANCE_SUCCESS } from '@/pages/SpaceCustomAppEditPage/actionTypes'
import {
  CREATE_NOTIFICATION_SOURCE_SUCCESS,
  DELETE_NOTIFICATION_SOURCE_SUCCESS,
  FETCH_NOTIFICATION_SOURCES_SUCCESS,
  UPDATE_NOTIFICATION_SOURCE_SUCCESS
} from '@/pages/SpaceNotificationSourcesPage/actionTypes'

type Space = {
  id: string
  name: string
  default: boolean
  description: string
  organizationId: string
  appInstances: any[]
  notificationSources: any[]
  notificationCategories: any[]
}

type SpacesState = {
  byId: Record<string, Space>
}

const initialState: SpacesState = { byId: {} }

export default function spacesReducer(state = initialState, action: AnyAction): SpacesState {
  switch (action.type) {
    case FETCH_CONTEXT_ORGANIZATION_SUCCESS: {
      return fetchCurrentOrganizationSuccess(state, action)
    }
    case DELETE_SPACE_SUCCESS: {
      return deleteSpace(state, action)
    }
    case CHANGE_DEFAULT_SPACE_SUCCESS: {
      return changeDefaultSpace(state, action)
    }
    case ADD_SPACE_SUCCESS: {
      return addSpace(state, action)
    }
    case ADD_CUSTOM_APP_INSTANCE_SUCCESS: {
      return addAppInstance(state, action.spaceId, action.response)
    }
    case UPDATE_SPACE_SUCCESS: {
      return updateSpace(state, action)
    }
    case FETCH_NOTIFICATION_SOURCES_SUCCESS: {
      return fetchNotificationSources(state, action)
    }
    case CREATE_NOTIFICATION_SOURCE_SUCCESS: {
      return createNotificationSource(state, action)
    }
    case DELETE_NOTIFICATION_SOURCE_SUCCESS: {
      return deleteNotificationSource(state, action)
    }
    case UPDATE_NOTIFICATION_SOURCE_SUCCESS: {
      return updateNotificationSource(state, action)
    }
    default: {
      return state
    }
  }
}

function deleteSpace(state: SpacesState, action: AnyAction) {
  const updatedById = { ...state.byId }
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete updatedById[action.spaceId]

  return { byId: updatedById }
}

function changeDefaultSpace(state: SpacesState, action: AnyAction) {
  const spaces = Object.keys(state.byId).map(id => state.byId[id])

  const updateDefaultSpaceForOrg = spaces.map(space => {
    return { ...space, default: action.spaceId === space.id }
  })

  const mapBackToById = updateDefaultSpaceForOrg.reduce(
    (byId, space) => {
      byId[space.id] = space
      return byId
    },
    {} as SpacesState['byId']
  )

  return {
    byId: {
      ...state.byId,
      ...mapBackToById
    }
  }
}

function addSpace(state: SpacesState, action: AnyAction) {
  return {
    byId: {
      ...state.byId,
      [action.response.id]: {
        ...action.response,
        organizationId: action.organizationId
      }
    }
  }
}

function updateSpace(state: SpacesState, action: AnyAction) {
  return {
    byId: {
      ...state.byId,
      [action.spaceId]: { ...state.byId[action.spaceId], ...action.response }
    }
  }
}

function addAppInstance(state: SpacesState, spaceId: string, appInstance: any) {
  const space = state.byId[spaceId]
  const appInstances = space.appInstances || []
  return {
    byId: {
      ...state.byId,
      [spaceId]: {
        ...space,
        appInstances: [appInstance, ...appInstances]
      }
    }
  }
}

function fetchCurrentOrganizationSuccess(state: SpacesState, action: AnyAction) {
  const byIdMapCO: Record<string, any> = {}

  action.response.spaces
    ?.map((space: any) => {
      return {
        ...space,
        organizationId: action.response.id
      }
    })
    .flat()
    .forEach((space: any) => {
      byIdMapCO[space.id] = {
        ...space,
        default: space.default,
        organizationId: space.organizationId
      }
    })

  return {
    byId: {
      ...state.byId,
      ...byIdMapCO
    }
  }
}

function fetchNotificationSources(state: SpacesState, action: AnyAction) {
  const space = state.byId[action.spaceId]

  return {
    byId: {
      ...state.byId,
      [action.spaceId]: {
        ...space,
        notificationSources: action.response
      }
    }
  }
}

function createNotificationSource(state: SpacesState, action: AnyAction) {
  const space = state.byId[action.spaceId]

  return {
    byId: {
      ...state.byId,
      [action.spaceId]: {
        ...space,
        notificationSources: [action.response, ...space.notificationSources]
      }
    }
  }
}

function deleteNotificationSource(state: SpacesState, action: AnyAction) {
  const space = state.byId[action.spaceId]

  return {
    byId: {
      ...state.byId,
      [action.spaceId]: {
        ...space,
        notificationSources: space.notificationSources.filter(
          (notificationSource: { id: string }) => notificationSource.id !== action.notificationSourceId
        )
      }
    }
  }
}

function updateNotificationSource(state: SpacesState, action: AnyAction) {
  const space = state.byId[action.spaceId]

  return {
    byId: {
      ...state.byId,
      [action.spaceId]: {
        ...space,
        notificationSources: space.notificationSources.map((notificationSource: any) => {
          if (notificationSource.id !== action.response.id) {
            return notificationSource
          }

          return {
            ...notificationSource,
            ...action.response
          }
        })
      }
    }
  }
}
